import {
  Stack,
  Text,
  Box,
  Heading,
  Skeleton,
  IconApartment,
  useDialogControllers,
  Dialog,
  Button,
  Inline,
  Columns,
  Column,
  IconEdit,
  TooltipRenderer,
  IconInfoInverted,
  IconDelete,
  ButtonIcon,
  useToast,
  useResponsiveValue,
  TextLink,
  Autosuggest
} from '@machobear/design-system'
import { ReactElement, useCallback, useMemo, useState, useRef, useEffect } from 'react'
import debounce from 'debounce-promise'
import * as Yup from 'yup'
import { Form, Formik, useFormikContext } from 'formik'
import { useParams, useHistory } from 'react-router'
import moment from 'moment'
import { omit, isEmpty, isNil } from 'ramda'
import { useTranslation, Trans } from 'react-i18next'
import queryClient from '@/components/query-client'
import { FieldInput, Dropdown } from '@/components/ds-form'
import { WarningLarge } from '@/components/icons'
import { getFormattedStates } from '@/utils/address'
import useBasic from '@/hooks/use-basic'

import { findAddress, findMoreAddress } from '@/models/profile/ports'
import {
  useSaveStandAloneTrustScore,
  useUpdateStandAloneTrustScore,
  useGetStandAloneTrustScoreSin,
  useGetStandAloneTrustScoreDl,
  useGetTenantConsent,
  useDeleteTenantConsent,
  useGetLookupCount,
  useRetrieveAddress,
  useUploadTenantConsent
} from '@/hooks/use-standalone-trust-score'
import { Dropzone } from '@/components/dropzone'
import { DateFieldInput } from '@/components/ds-form/date-field'
import WarningModal from '@/components/modals/warning-modal'

import { openBlobInNewTab, dataURItoBlob } from '@/utils/files'

import { PasswordField } from '@/components/ds-form/password-field'
import Illustration from './illustration.svg?react'
import FormFooter from './form-footer'
import { FormBox, FormRow } from './form-sections'
import FormWrapper from './form-wrapper'
import { useUserIdState } from './hook/useUserIdState'

const CreditCheckForm = ({ data, isLoading }: { data: any; isLoading: boolean }) => {
  const { t } = useTranslation()
  const nameColumnRef = useRef<HTMLDivElement>(null)
  const { trustScoreId = '' } = useParams<{ trustScoreId: string }>()
  const history = useHistory()
  const showToast = useToast()
  const { isUserIdUnverified } = useUserIdState()
  const [enterAddressManually, setEnterAddressManually] = useState(false)

  const {
    data: lookupCount,
    isLoading: isLoadingLookupCount,
    refetch: refetchLookup
  } = useGetLookupCount()

  const maxAttemptsReached = lookupCount === 0

  const { mutateAsync: retrieveAddress, isPending: isRetrievingAddress } = useRetrieveAddress()

  const { mutateAsync: saveStandAloneTrustScore, isPending: isSavingTrustScore } =
    useSaveStandAloneTrustScore()

  const { mutateAsync: updateStandAloneTrustScore, isPending: isUpdatingTrustScore } =
    useUpdateStandAloneTrustScore(trustScoreId)

  const { countries }: any = useBasic(['countries'])
  const formattedStates = getFormattedStates(countries, 'ca')

  const [suggestions, setSuggestions] = useState<
    { value: string; text: string; icon: ReactElement }[]
  >([])
  const [showSeachBar, setShowSearchBar] = useState<boolean>(false)

  const minDate = moment().subtract(100, 'years').toDate()
  const maxDate = moment().subtract(13, 'years').toDate()

  const { data: tenantConsentData } = useGetTenantConsent(trustScoreId)
  const { mutateAsync: uploadTenantConsent } = useUploadTenantConsent()
  const { mutate } = useDeleteTenantConsent()

  const { visible, showModal, closeModal } = useDialogControllers({
    enterManuallyWarningDialog: false,
    lookupCountDialog: false
  })

  const creditCheckSubmitValidationSchema = Yup.object().shape({
    first_name: Yup.string()
      .matches(
        /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
        t('b.profile.name.special_characters.error', 'Cannot include special characters')
      )
      .max(255)
      .required(),
    last_name: Yup.string()
      .matches(
        /^[ A-Za-z-_\u3000\u3400-\u4DBF\u4E00-\u9FFF]*$/,
        t('b.profile.name.special_characters.error', 'Cannot include special characters')
      )
      .max(255)
      .required(),
    dob: Yup.date()
      .min(minDate, 'Invalid date of birth')
      .max(maxDate, 'Invalid date of birth')
      .required(),
    full_address: Yup.mixed().when('street_name', {
      is: (value: string) => (isNil(value) || value === '') && !enterAddressManually,
      then: Yup.object()
        .nullable()
        .shape({
          text: Yup.string().required(),
          value: Yup.string().required()
        })
        .required(),
      otherwise: Yup.object().nullable().shape({
        text: Yup.string(),
        value: Yup.string()
      })
    }),
    street_number: Yup.string().required(),
    street_name: Yup.string().required(),
    city: Yup.string().required(),
    province: Yup.string().required(),
    postal_code: Yup.string()
      .matches(/^[a-zA-Z0-9\s]*$/, 'Can only contain letters and numbers')
      .transform(value => value.replace(/\s+/g, ''))
      .when(['country'], (country, schema) => {
        if (country === 'ca') {
          return schema
            .length(6)
            .matches(
              /^[ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ][ -]?[0-9][ABCEGHJKLMNPRSTVWXYZ][0-9]$/,
              'Must be a valid postal code'
            )
            .required()
        }
        if (country === 'us') {
          return schema.length(5).required()
        }
        return schema.required()
      }),
    country: Yup.string().required(),
    sin: Yup.string()
      .matches(/^\S+$/, 'Cannot include spaces')
      .matches(/^\d*$/, 'Cannot include letters or special characters')
      .matches(/^\d{9}$/, 'Invalid SIN number'),
    driver_license: Yup.string(),
    tenant_consent: tenantConsentData?.consent ? Yup.string() : Yup.array()
  })

  const handleEnterAddressManually = () => {
    setEnterAddressManually(true)
    setShowSearchBar(false)
    closeModal('enterManuallyWarningDialog')()
  }

  const showWanirng = () => {
    return showToast({
      message: t(
        'l.standalone_trustscore.form_verification_required_title',
        'ID Verification Needed'
      ),
      description: t(
        'l.standalone_trustscore.form_verification_required_description',
        'We require additional information to verify your account before you can continue using the Trust Score.'
      ),
      tone: 'critical'
    })
  }

  const handleCreditCheckSubmit = async (values: any) => {
    if (isUserIdUnverified) return showWanirng()

    const creditCheckData = {
      ...data,
      tenant: {
        basic_info: {
          first_name: values.first_name,
          last_name: values.last_name,
          dob: isNil(values.dob) ? null : moment(values.dob).format('YYYY-MM-DD')
        },
        identity: {
          sin: values.sin === '' ? null : values.sin,
          driver_license: values.driver_license ? values.driver_license : null
        },
        address: {
          unit_number: !!values.unit_number ? values.unit_number : null,
          street_number: values.street_number,
          street_name: values.street_name,
          city: values.city,
          province: values.province,
          postal_code: values.postal_code.replace(/\s+/g, ''),
          country: values.country
        }
      }
    }

    const caller = trustScoreId ? updateStandAloneTrustScore : saveStandAloneTrustScore

    const result = await caller(creditCheckData)
    const id = result?.body.tenant.basic_info.id

    if (result?.response.ok) {
      if (!tenantConsentData?.consent && !isEmpty(values.tenant_consent)) {
        const fileResult = await uploadTenantConsent({
          fileData: values.tenant_consent[0],
          trustScoreId: id
        })

        if (fileResult?.response.ok) {
          history.push(`/trust-score/edit/${id}/tenant-screening`)

          return showToast({
            message: t('l.standalone_trustscore.form.change_saved', 'Trust Score changes saved!'),
            tone: 'positive'
          })
        }
      } else {
        history.push(`/trust-score/edit/${id}/tenant-screening`)

        return showToast({
          message: t('l.standalone_trustscore.form.change_saved', 'Trust Score changes saved!'),
          tone: 'positive'
        })
      }
    }
  }

  const initialValues = {
    first_name: data?.tenant?.basic_info?.first_name || '',
    last_name: data?.tenant?.basic_info?.last_name || '',
    dob: data?.tenant?.basic_info?.dob ? moment(data?.tenant?.basic_info?.dob).toDate() : null,
    full_address: {
      text: '',
      value: ''
    },
    street_number: data?.tenant?.address?.street_number,
    street_name: data?.tenant?.address?.street_name || '',
    unit_number: data?.tenant?.address?.unit_number || '',
    city: data?.tenant?.address?.city || '',
    province: data?.tenant?.address?.province || '',
    postal_code: data?.tenant?.address?.postal_code || '',
    country: data?.tenant?.address?.country?.toLowerCase() || 'ca',
    sin: data?.tenant?.identity?.sin || '',
    driver_license: data?.tenant?.identity?.driver_license || '',
    tenant_consent: tenantConsentData?.consent || []
  }

  useEffect(() => {
    if (maxAttemptsReached) {
      setEnterAddressManually(true)
      setShowSearchBar(false)
    } else if (data?.tenant?.address.street_name === '') {
      setShowSearchBar(true)
    }
  }, [isLoadingLookupCount])

  const requiredFieldsForSave = ['first_name', 'last_name']
  const otherFields = [
    'dob',
    'full_address',
    'street_number',
    'street_name',
    'city',
    'province',
    'postal_code',
    'country',
    'sin',
    'tenant_consent'
  ]

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const generateAddressSuggestions = useCallback(async (address: string) => {
    if (address === '') {
      setSuggestions([])
      return
    }
    const result = await findAddress({ address })

    if (result?.response.ok) {
      const data = result?.body.map((item: any) => ({
        icon: <IconApartment />,
        text: `${item?.Text} ${item?.Description}`,
        value: item.Id,
        fetchSubItems: item.Next === 'Find' ? () => findAddressUnitList(item.Id) : null
      }))

      setSuggestions(data)
    }
  }, [])
  const findAddressUnitList = async (id: string) => {
    const result = await findMoreAddress({ lastId: id })

    if (result?.response.ok) {
      const data = result?.body.map((item: any) => ({
        icon: <IconApartment />,
        text: `${item?.Text} ${item?.Description}`,
        value: item.Id
      }))

      return data
    }
  }

  const debouncedGenerateAddressSuggestions = useMemo(() => {
    return debounce(generateAddressSuggestions, 500)
  }, [generateAddressSuggestions])

  return (
    <Formik
      onSubmit={handleCreditCheckSubmit}
      initialValues={initialValues}
      validationSchema={creditCheckSubmitValidationSchema}
      enableReinitialize={!!trustScoreId}
      validateOnMount={true}
    >
      {({
        values,
        setFieldValue,
        setValues,
        validateForm,
        setFieldError,
        setTouched,
        errors,
        touched
      }) => {
        const isAddressRegistered = !!values?.street_name
        const hasFileBeenUploaded = tenantConsentData?.consent

        const handleConfirmAddress = async () => {
          const result = await retrieveAddress(values.full_address?.value)

          const { address } = result?.body

          setValues({
            ...values,
            country: address.country_code.toLowerCase(),
            postal_code: address?.zip,
            province: address?.state_code,
            city: address?.city,
            street_name: address?.street_name,
            street_number: address?.street_number,
            unit_number: address?.unit_number || null
          })
          if (!enterAddressManually) {
            setShowSearchBar(false)
          }

          if (result?.response.ok) {
            refetchLookup()
            if (lookupCount === 1) {
              showToast({
                message: t('l.standalone_trustscore.form.address_warning_title', 'Limit reached'),
                description: t(
                  'l.standalone_trustscore.form.address_warning_description_2',
                  'Too many address attempts. Please enter address manually or try again later.'
                ),
                tone: 'critical'
              })
            }
            closeModal('lookupCountDialog')()
          } else {
            showToast({
              message: result?.body?.message,
              tone: 'critical'
            })
          }
        }
        const handleCloseAddressModal = () => {
          setSuggestions([])
          closeModal('lookupCountDialog')()
        }

        const handleAddressChange = async (address: any) => {
          if (visible.lookupCountDialog) return

          setFieldValue('full_address', { text: address.text, value: address.value })
          address?.value
            ? showModal('lookupCountDialog')()
            : debouncedGenerateAddressSuggestions(address?.text)
        }

        const handleOnSave = async () => {
          await setTouched({ first_name: true, last_name: true }, false)
          const errors = await validateForm()
          const newErrors = omit(otherFields, errors) ?? {}

          if (!isEmpty(newErrors)) {
            await Object.keys(newErrors).forEach(field => {
              const isRequiredField = requiredFieldsForSave.includes(field)
              const vals: { [key: string]: any } = values

              if (isRequiredField) {
                setFieldError(
                  field,
                  vals[field] === ''
                    ? t('common.required', 'Required')
                    : t(
                        'b.profile.name.special_characters.error',
                        'Cannot include special characters'
                      )
                )
              } else {
                setFieldError(field, '')
              }
            })

            return await nameColumnRef?.current?.scrollIntoView({
              behavior: 'scrollBehavior' in document.documentElement.style ? 'smooth' : 'auto',
              block: 'center'
            })
          }

          const tenantData = {
            ...data,
            tenant: {
              basic_info: {
                first_name: values.first_name,
                last_name: values.last_name,
                dob: isNil(values.dob) ? null : moment(values.dob).format('YYYY-MM-DD')
              },
              identity: {
                sin: values.sin === '' ? null : values.sin,
                driver_license: values.driver_license ? values.driver_license : null
              }
            }
          }

          const dataWithAddress = {
            tenant: {
              ...tenantData.tenant,
              address: {
                unit_number: !!values.unit_number ? values.unit_number : null,
                street_number: values.street_number,
                street_name: values.street_name,
                city: values.city,
                province: values.province,
                postal_code: values.postal_code,
                country: values.country
              }
            }
          }

          const isAddressAdded = values.street_name && values.street_number && values.city
          const creditCheckData = isAddressAdded ? dataWithAddress : tenantData

          const caller = trustScoreId ? updateStandAloneTrustScore : saveStandAloneTrustScore

          const result = await caller(creditCheckData)

          if (result?.response.ok) {
            if (!tenantConsentData?.consent && !isEmpty(values.tenant_consent)) {
              const id = result.body.tenant.basic_info.id
              const fileResult = await uploadTenantConsent({
                fileData: values.tenant_consent[0],
                trustScoreId: id
              })

              if (fileResult?.response.ok) {
                history.push('/trust-score')
                return showToast({
                  message: t(
                    'l.standalone_trustscore.form.change_saved',
                    'Trust Score changes saved!'
                  ),
                  tone: 'positive'
                })
              } else {
                return showToast({
                  message: 'Something went wrong. Please try again',
                  tone: 'critical'
                })
              }
            } else {
              history.push('/trust-score')
              return showToast({
                message: t(
                  'l.standalone_trustscore.form.change_saved',
                  'Trust Score changes saved!'
                ),
                tone: 'positive'
              })
            }
          }
        }

        const showChangeAddressOption = (!isLoading && isAddressRegistered) || enterAddressManually
        const showAddressSearch = (!enterAddressManually && !isAddressRegistered) || showSeachBar

        const handleRemoveFile = (file: File) => {
          setFieldValue('tenant_consent', [])
        }

        const handleDeleteTenantConsent = () => {
          mutate(trustScoreId, {
            onSuccess: async () => {
              setFieldValue('tenant_consent', [])
              queryClient.invalidateQueries({ queryKey: ['tenant-consent', trustScoreId] })
            }
          })
        }

        const handleChangeAddressClick = () => {
          setFieldValue('full_address', { text: '', value: '' })
          setShowSearchBar(true)
          setEnterAddressManually(false)
        }

        return (
          <FormWrapper>
            <Form>
              <Dialog
                width="xsmall"
                id="addressDialog"
                illustration={
                  <Box marginTop={'large'}>
                    <Illustration />
                  </Box>
                }
                open={visible.lookupCountDialog}
                onClose={handleCloseAddressModal}
                title={`${lookupCount} attempt(s) left`}
                description={
                  <Stack align="center" space="small">
                    <Text tone="secondary" align={'center'}>
                      {t(
                        'l.standalone_trustscore.form_confirm_address',
                        'Please confirm the selected address:'
                      )}
                    </Text>
                    <Text tone="secondary" align={'center'} weight="medium">
                      {values.full_address.text}
                    </Text>
                  </Stack>
                }
              >
                <Columns space="small">
                  <Column width="1/2">
                    <Button variant="transparent" onClick={handleCloseAddressModal}>
                      {t('common.cancel', 'Cancel')}
                    </Button>
                  </Column>
                  <Column>
                    <Button
                      onClick={handleConfirmAddress}
                      disabled={isRetrievingAddress}
                      loading={isRetrievingAddress}
                    >
                      {t('common.confirm', 'Confirm')}
                    </Button>
                  </Column>
                </Columns>
              </Dialog>
              <WarningModal
                title={t(
                  'l.standalone_trustscore.form.manually_enter_address_warning.title',
                  'Are you sure?'
                )}
                onClose={closeModal('enterManuallyWarningDialog')}
                description={
                  <Box display="flex" alignItems="center" justifyContent="center">
                    <Text align="center" tone="secondary">
                      {t(
                        'l.standalone_trustscore.form.manually_enter_address_warning.description',
                        'Manually adding the address may affect accuracy in confirming a match with the tenant’s Equifax® records.'
                      )}
                    </Text>
                  </Box>
                }
                ctaAction={handleEnterAddressManually}
                ctaText={t('common.confirm', 'Confirm')}
                visible={visible.enterManuallyWarningDialog}
                illustration={<WarningLarge />}
                cancelButton
                cancelText={t('common.cancel', 'Cancel')}
              />
              <Stack align="left" space="gutter">
                {!isMobile && (
                  <Heading level="4">
                    {t('r.profile.edit.credit_check.title', 'Credit Check')}
                  </Heading>
                )}

                <FormBox
                  label={t('l.standalone_trustscore.form.tenant_details_title', 'Tenant Details')}
                >
                  <FormRow
                    label={t('r.profile.edit.basic_information.title', 'Basic Information')}
                    description={t(
                      'l.standalone_trustscore.form.basic_information.description',
                      'Please make sure the information matches the tenant’s government-issued ID.'
                    )}
                  >
                    {isLoading ? (
                      <Skeleton.Shape height={58} />
                    ) : (
                      <Stack space="gutter" align="left">
                        <Box ref={nameColumnRef}>
                          <Columns space="gutter" collapseBelow={'desktop'}>
                            <Column width="1/2">
                              <FieldInput
                                autoComplete="new-text"
                                name="first_name"
                                label={t(
                                  'r.profile.edit.credit_check_first_name.label',
                                  'Legal first Name'
                                )}
                                required
                              />
                            </Column>
                            <Column width="1/2">
                              <FieldInput
                                autoComplete="new-text"
                                name="last_name"
                                label={t(
                                  'r.profile.edit.credit_check_last_name.label',
                                  'Legal last Name'
                                )}
                                required
                              />
                            </Column>
                          </Columns>
                        </Box>
                        <Columns space="gutter" collapseBelow="desktop">
                          <Column width="1/2">
                            <DateFieldInput
                              id="dob"
                              minDate={minDate}
                              maxDate={maxDate}
                              name="dob"
                              value={values.dob}
                              label={t(
                                'r.profile.edit.credit_check_date_of_birth.label',
                                'Date of birth'
                              )}
                              tone={touched.dob && errors?.dob ? 'critical' : 'neutral'}
                              required
                              disabled={false}
                            />
                          </Column>
                        </Columns>
                      </Stack>
                    )}
                  </FormRow>

                  {!isLoadingLookupCount && (
                    <FormRow
                      label={t('r.profile.view.current_address.label', 'Current Address')}
                      optionText={
                        showChangeAddressOption && !isLoadingLookupCount ? (
                          <Box
                            onClick={handleChangeAddressClick}
                            role="button"
                            cursor="pointer"
                            marginTop={{ mobile: 'small', tablet: 'none' }}
                            disabled={maxAttemptsReached}
                            aria-disabled={maxAttemptsReached}
                            pointerEvents={maxAttemptsReached ? 'none' : undefined}
                          >
                            <Inline align="left" space="xxsmall" alignY="center">
                              <IconEdit
                                tone={maxAttemptsReached ? 'disabled' : 'link'}
                                size="xsmall"
                              />

                              <Text weight="medium" tone={maxAttemptsReached ? 'disabled' : 'link'}>
                                <Trans
                                  i18nKey="l.standalone_trustscore.form.basic.change_address_count"
                                  t={t}
                                  lookupCount={lookupCount}
                                >
                                  Change address ({{ lookupCount }} attempts left)
                                </Trans>
                              </Text>
                            </Inline>
                          </Box>
                        ) : null
                      }
                    >
                      {isLoading ? (
                        <Skeleton.Shape height={58} />
                      ) : (
                        <>
                          {showAddressSearch && (
                            <>
                              <Box
                                position="relative"
                                marginBottom={enterAddressManually ? 'medium' : 'none'}
                              >
                                <Autosuggest
                                  scrollToTopOnMobile={false}
                                  type="search"
                                  id="full_address"
                                  name="full_address"
                                  label={t(
                                    'l.standalone_trustscore.form.basic_information.full_address',
                                    'Full address'
                                  )}
                                  suggestions={suggestions}
                                  value={{ text: values.full_address.text }}
                                  onChange={handleAddressChange}
                                  message={
                                    touched.full_address && errors.street_name
                                      ? t('common.required', 'Required')
                                      : t(
                                          'l.standalone_trustscore.form.basic_information.full_address_warning',
                                          'For your security, we only allow a maximum of 3 address change attempts.'
                                        )
                                  }
                                  required={!enterAddressManually}
                                  tone={
                                    touched.full_address && errors.full_address
                                      ? 'critical'
                                      : 'neutral'
                                  }
                                  hideSuggestionsOnSelection={false}
                                  noSuggestionsMessage={t(
                                    'l.standalone_trustscore.form.basic_information.full_address_no_suggestion',
                                    `Can't find your address? Contact support@liv.rent`
                                  )}
                                />
                              </Box>
                              {!enterAddressManually && !isAddressRegistered && (
                                <Box display="flex">
                                  <Box
                                    onClick={showModal('enterManuallyWarningDialog')}
                                    role="button"
                                    cursor="pointer"
                                    marginTop={{ mobile: 'small', tablet: 'none' }}
                                  >
                                    <Inline align="left" space="xxsmall" alignY="center">
                                      <Text weight="medium" tone="link">
                                        <Trans
                                          i18nKey="l.standalone_trustscore.form.basic.enter_address_manually"
                                          t={t}
                                        >
                                          Add address manually
                                        </Trans>
                                      </Text>
                                    </Inline>
                                  </Box>
                                </Box>
                              )}
                            </>
                          )}

                          {(enterAddressManually || isAddressRegistered) && (
                            <>
                              <Columns space="gutter" collapseBelow="tablet">
                                <Column width="1/2">
                                  <FieldInput
                                    disabled={isAddressRegistered && !enterAddressManually}
                                    name="street_number"
                                    label={t(
                                      'l.standalone_trustscore.form.basic_information.streer_number',
                                      'Street number'
                                    )}
                                    required
                                  />
                                </Column>
                                <Column width="1/2">
                                  <FieldInput
                                    disabled={isAddressRegistered && !enterAddressManually}
                                    name="street_name"
                                    label={t(
                                      'l.standalone_trustscore.form.basic_information.streer_name',
                                      'Street name'
                                    )}
                                    required
                                  />
                                </Column>
                              </Columns>
                              <Columns space="gutter" collapseBelow="tablet">
                                <Column width="1/2">
                                  <FieldInput
                                    disabled={isAddressRegistered && !enterAddressManually}
                                    name="unit_number"
                                    label={t(
                                      'l.standalone_trustscore.form.basic_information.unit_number',
                                      'Unit number'
                                    )}
                                  />
                                </Column>
                                <Column width="1/2">
                                  <FieldInput
                                    disabled={isAddressRegistered && !enterAddressManually}
                                    name="city"
                                    label={t(
                                      'l.standalone_trustscore.form.basic_information.city',
                                      'City'
                                    )}
                                    required
                                  />
                                </Column>
                              </Columns>
                              <Columns space="gutter" collapseBelow="tablet">
                                <Column width="1/2">
                                  <Dropdown
                                    name="province"
                                    disabled={isAddressRegistered && !enterAddressManually}
                                    label={t(
                                      'l.standalone_trustscore.form.basic_information.province',
                                      'Province'
                                    )}
                                    aria-labelledby="province"
                                    required
                                    options={formattedStates}
                                  />
                                </Column>
                                <Column width="1/2">
                                  <FieldInput
                                    disabled={isAddressRegistered && !enterAddressManually}
                                    name="postal_code"
                                    label={t(
                                      'l.standalone_trustscore.form.basic_information.postal',
                                      'Postal code'
                                    )}
                                    required
                                  />
                                </Column>
                              </Columns>
                              <Columns space="gutter" collapseBelow="tablet">
                                <Column width="1/2">
                                  <Dropdown
                                    name="country"
                                    aria-labelledby="country"
                                    label={t('b.checkout.field.country', 'Country')}
                                    required
                                    disabled
                                    options={[{ value: 'ca', label: 'Canada' }]}
                                  />
                                </Column>
                              </Columns>

                              {!enterAddressManually && (
                                <Box display="flex">
                                  <Box
                                    onClick={showModal('enterManuallyWarningDialog')}
                                    role="button"
                                    cursor="pointer"
                                    marginTop={{ mobile: 'small', tablet: 'none' }}
                                  >
                                    <Inline align="left" space="xxsmall" alignY="center">
                                      <Text weight="medium" tone="link">
                                        <Trans
                                          i18nKey="l.standalone_trustscore.form.basic.enter_address_manually"
                                          t={t}
                                        >
                                          Add address manually
                                        </Trans>
                                      </Text>
                                    </Inline>
                                  </Box>
                                </Box>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </FormRow>
                  )}
                  <FormRow
                    label={t(
                      'l.standalone_trustscore.form.identity_details_title',
                      'Identity Details'
                    )}
                    tooltip={
                      !isLoading && (
                        <TooltipRenderer
                          id="sin-tooltip"
                          placement="top"
                          tooltip={
                            <Text>
                              {t(
                                'l.standalone_trustscore.form.basic_information.sin_tooltip1',
                                'Adding a Social Insurance Number (SIN) or Driver’s License Number helps ensure accuracy with verifying your identity when pulling credit information from Equifax®. All user information is securely stored and is deleted after submitting.'
                              )}
                            </Text>
                          }
                        >
                          {({ triggerProps }) => (
                            <Inline space="none" align="left">
                              <Box aria-label="Help" {...triggerProps}>
                                <Inline space="xxsmall" alignY="center">
                                  <IconInfoInverted tone="info" />
                                  <Text tone="info" weight="medium">
                                    {t(
                                      'l.standalone_trustscore.form.basic_information.sin_tooltip2',
                                      'Why do we need this?'
                                    )}
                                  </Text>
                                </Inline>
                              </Box>
                            </Inline>
                          )}
                        </TooltipRenderer>
                      )
                    }
                  >
                    {isLoading ? (
                      <Skeleton.Shape height={58} />
                    ) : (
                      <Stack space="gutter" align="left">
                        <SINInput value={values.sin} trustScoreId={trustScoreId} />
                        <DLInput value={values.driver_license} trustScoreId={trustScoreId} />
                      </Stack>
                    )}
                  </FormRow>
                </FormBox>
                <FormBox
                  label={t('l.standalone_trustscore.form.tenant_consent_title', 'Tenant Consent')}
                  description={t(
                    'l.standalone_trustscore.form.tenant_consent_description',
                    'Please provide proof of your applicant’s consent for credit check, such as a separate application form that they have filled out.'
                  )}
                >
                  {isLoading ? (
                    <Skeleton.Shape height={58} />
                  ) : !hasFileBeenUploaded || isEmpty(values.tenant_consent) ? (
                    <>
                      <Dropzone
                        id="tenant-consent"
                        accept={{
                          'image/jpeg': [],
                          'image/png': [],
                          'application/pdf': []
                        }}
                        files={values.tenant_consent}
                        onChange={files => {
                          setFieldValue('tenant_consent', files)
                        }}
                        onRemoveFile={handleRemoveFile}
                        tone={
                          errors?.tenant_consent && touched?.tenant_consent ? 'critical' : 'neutral'
                        }
                        message={touched?.tenant_consent && errors?.tenant_consent?.toString()}
                      />
                      <Box padding="small" background="infoLight" borderRadius="large">
                        <Columns space="xxsmall" alignY={{ mobile: 'top', tablet: 'center' }}>
                          <Column width="content">
                            <IconInfoInverted tone="info" />
                          </Column>
                          <Column>
                            <Trans
                              i18nKey="l.standalone_trustscore.form.credit_report_consnt_form"
                              t={t}
                            >
                              <Text tone="info" size="small">
                                Don't have one? Download our{' '}
                                <TextLink
                                  href={'/pdfs/livrent_credit_report_consent_form_v1.0.6.pdf'}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  weight="regular"
                                >
                                  Credit Report Consent form
                                </TextLink>
                                .
                              </Text>
                            </Trans>
                          </Column>
                        </Columns>
                      </Box>
                    </>
                  ) : (
                    <Box
                      paddingX="small"
                      paddingY="medium"
                      display="flex"
                      justifyContent="spaceBetween"
                      boxShadow="borderNeutralLight"
                      borderRadius="small"
                      alignItems="center"
                    >
                      <Box
                        aria-label="View document"
                        onClick={() => openBlobInNewTab(dataURItoBlob(values.tenant_consent))}
                        cursor="pointer"
                        role="button"
                      >
                        <Text size="medium" weight="medium" tone="link">
                          {t('l.application.view.view_document.label', 'View document')}
                        </Text>
                      </Box>
                      <ButtonIcon
                        id="delete-tenant-consent"
                        label="Delete file"
                        variant="transparent"
                        onClick={handleDeleteTenantConsent}
                        icon={<IconDelete tone="critical" />}
                      />
                    </Box>
                  )}
                </FormBox>
                <FormFooter
                  onSave={handleOnSave}
                  saveCta={t('l.standalone_trustscore.form.save_as_draft', 'Save as draft')}
                  isLoading={isLoading}
                  isSubmitting={isUpdatingTrustScore || isSavingTrustScore}
                />
              </Stack>
            </Form>
          </FormWrapper>
        )
      }}
    </Formik>
  )
}

const SINInput = ({ value, trustScoreId }: { value: string; trustScoreId: string }) => {
  const { t } = useTranslation()
  const { mutateAsync: getStandAloneTrustScoreSin, isPending: isLoadingSin } =
    useGetStandAloneTrustScoreSin()
  const formik = useFormikContext()

  const revealSin = async () => {
    const result = await getStandAloneTrustScoreSin(trustScoreId)
    formik.setFieldValue('sin', result.sin)
  }

  useEffect(() => {
    if (value === '*** *** ***') {
      revealSin()
    }
  }, [value])

  return isLoadingSin ? (
    <Skeleton.Shape height={58} />
  ) : (
    <PasswordField
      name="sin"
      label={t(
        'l.standalone_trustscore.form.basic_information.sin_number',
        'SIN number (Optional)'
      )}
      visibilityToggleLabel="Toggle visibility"
    />
  )
}

const DLInput = ({ value, trustScoreId }: { value: string; trustScoreId: string }) => {
  const { t } = useTranslation()
  const { mutateAsync: getStandAloneTrustScoreDl, isPending: isLoadingDl } =
    useGetStandAloneTrustScoreDl()
  const formik = useFormikContext()

  const revealDl = async () => {
    const result = await getStandAloneTrustScoreDl(trustScoreId)
    formik.setFieldValue('driver_license', result.driver_license)
  }

  useEffect(() => {
    if (value.includes('*')) {
      revealDl()
    }
  }, [value])

  return isLoadingDl ? (
    <Skeleton.Shape height={58} />
  ) : (
    <PasswordField
      name="driver_license"
      label={t(
        'l.standalone_trustscore.form.basic_information.dl_number',
        "Driver's License number (Optional)"
      )}
      visibilityToggleLabel="Toggle visibility"
    />
  )
}

export default CreditCheckForm
